import { Loader2, Inbox, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fileUrl, initials } from "@/lib/api";

export const PageLoader = () => (
  <div className="flex min-h-[50vh] items-center justify-center" data-testid="page-loader">
    <Loader2 className="h-8 w-8 animate-spin text-[#1F6FFF]" />
  </div>
);

export const Spinner = ({ className = "h-4 w-4" }) => <Loader2 className={`${className} animate-spin`} />;

export const EmptyState = ({ title, text, action, testid = "empty-state" }) => (
  <div data-testid={testid} className="flex flex-col items-start gap-3 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10">
    <div className="rounded-xl bg-[#1F6FFF]/10 p-3 text-[#1F6FFF]"><Inbox className="h-6 w-6" /></div>
    <h4 className="text-lg font-semibold text-[#0B1A33]">{title}</h4>
    {text && <p className="max-w-md text-sm text-slate-500">{text}</p>}
    {action}
  </div>
);

const STATUS = {
  approved: "bg-[#3CC34A]/12 text-[#1f8f2b] border-[#3CC34A]/30",
  completed: "bg-[#3CC34A]/12 text-[#1f8f2b] border-[#3CC34A]/30",
  deducted: "bg-slate-100 text-slate-700 border-slate-200",
  present: "bg-[#3CC34A]/12 text-[#1f8f2b] border-[#3CC34A]/30",
  sent: "bg-[#3CC34A]/12 text-[#1f8f2b] border-[#3CC34A]/30",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  queued: "bg-amber-50 text-amber-700 border-amber-200",
  reserved: "bg-amber-50 text-amber-700 border-amber-200",
  scheduled: "bg-[#1F6FFF]/10 text-[#1450C9] border-[#1F6FFF]/25",
  in_progress: "bg-cyan-50 text-cyan-700 border-cyan-200",
  rejected: "bg-red-50 text-red-600 border-red-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
  failed: "bg-red-50 text-red-600 border-red-200",
  absent: "bg-red-50 text-red-600 border-red-200",
  inactive: "bg-slate-100 text-slate-500 border-slate-200",
};

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
    {status === "in_progress" && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cyan-500" />}
    {String(status || "").replace(/_/g, " ")}
  </span>
);

export const StatCard = ({ label, value, icon: Icon, tone = "blue", testid, hint }) => {
  const tones = { blue: "bg-[#1F6FFF]/10 text-[#1F6FFF]", green: "bg-[#3CC34A]/12 text-[#2DA638]",
    amber: "bg-amber-100 text-amber-600", navy: "bg-[#0B1A33] text-[#3CC34A]", cyan: "bg-cyan-100 text-cyan-700" };
  return (
    <div className="lift rise rounded-2xl border border-slate-200/80 bg-white p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <div className={`rounded-xl p-2.5 ${tones[tone]}`}><Icon className="h-5 w-5" /></div>}
      </div>
      <p className="font-display mt-3 text-3xl font-extrabold text-[#0B1A33]" data-testid={testid ? `${testid}-value` : undefined}>{value ?? "—"}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div className="rise">
      <h1 className="text-2xl font-extrabold text-[#0B1A33] sm:text-3xl" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500 sm:text-base">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Avatar = ({ src, name, size = "h-10 w-10", className = "" }) =>
  src ? (
    <img src={fileUrl(src)} alt={name} className={`${size} shrink-0 rounded-full object-cover ${className}`} />
  ) : (
    <div className={`${size} flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#1F6FFF] to-[#3CC34A] text-sm font-bold text-white ${className}`}>
      {initials(name)}
    </div>
  );

export const SearchInput = ({ value, onChange, placeholder = "Search...", testid = "search-input" }) => (
  <div className="relative w-full sm:max-w-xs">
    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10 rounded-full bg-white pl-9" />
  </div>
);

export const Pagination = ({ page, pages, total, onPage }) => (
  <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-500" data-testid="pagination">
    <span data-testid="pagination-info">Page {page} of {pages} · {total} records</span>
    <div className="flex gap-2">
      <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" /></Button>
      <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pagination-next"><ChevronRight className="h-4 w-4" /></Button>
    </div>
  </div>
);

export const Panel = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-slate-200/80 bg-white ${className}`}>{children}</div>
);

export const DemoBanner = ({ text }) => (
  <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-amber-800" data-testid="demo-mode-banner">
    Demo Mode · {text}
  </div>
);
